// プラクティスバトル画面 (= DDSIM-22、 ADR-0026 §C/§E)。
// SOLO 動線の setup 画面。左右対称 VS layout でデッキ選択 + 手番を設定し /match へ渡す。
// CPU は常にプレイヤーと同じデッキ (mirror)。 /deck を吸収。

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { SAMPLE_DECKS } from "@/lib/sampleDecks";

// engine の MIN_DECK_SIZE=40 / MAX_DECK_SIZE=60 に準拠
const MIN_DECK_SIZE = 40;
const MAX_DECK_SIZE = 60;

type FirstPlayerChoice = "first" | "second" | "random";

function isDeckValid(len: number): boolean {
  return len >= MIN_DECK_SIZE && len <= MAX_DECK_SIZE;
}

export function Practice(): JSX.Element {
  const navigate = useNavigate();
  const [selectedDeckIndex, setSelectedDeckIndex] = useState<number>(0);
  const [firstPlayerChoice, setFirstPlayerChoice] =
    useState<FirstPlayerChoice>("random");

  const selectedDeck = SAMPLE_DECKS[selectedDeckIndex];

  function handleBattle(): void {
    let firstPlayer: number;
    if (firstPlayerChoice === "first") {
      firstPlayer = 0;
    } else if (firstPlayerChoice === "second") {
      firstPlayer = 1;
    } else {
      firstPlayer = Math.random() < 0.5 ? 0 : 1;
    }
    navigate("/match", {
      state: {
        deckId: selectedDeck.id,
        cards: selectedDeck.cards,
        firstPlayer,
      },
    });
  }

  return (
    <main className="min-h-screen bg-background flex flex-col p-6 gap-4">
      {/* ヘッダー */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate("/")}>
          ← MENU
        </Button>
        <h1 className="text-xl font-semibold tracking-tight">
          プラクティスバトル
        </h1>
      </div>

      {/* VS layout: 左(PLAYER) + 中央(手番/VS/BATTLE) + 右(CPU) */}
      <div className="flex flex-1 gap-6 mt-2">
        {/* 左カラム: PLAYER */}
        <div className="flex-1 flex flex-col gap-4">
          {/* デッキ preview プレースホルダ */}
          <div className="border border-border rounded-md flex-1 min-h-32 flex items-center justify-center text-muted-foreground text-sm">
            準備中
          </div>

          <div>
            <p className="text-xs font-semibold text-cyan-500 mb-1">PLAYER</p>
            <p className="text-sm text-muted-foreground mb-2">使用デッキ</p>

            {/* デッキ selector */}
            <div className="flex flex-col gap-1">
              {SAMPLE_DECKS.map((deck, i) => {
                const selected = i === selectedDeckIndex;
                const valid = isDeckValid(deck.cards.length);
                return (
                  <button
                    key={deck.id}
                    className={[
                      "text-left px-3 py-2 rounded border text-sm transition-colors",
                      selected
                        ? "border-cyan-500 border-l-4 bg-cyan-50 dark:bg-cyan-950"
                        : "border-border hover:bg-muted",
                    ].join(" ")}
                    onClick={() => setSelectedDeckIndex(i)}
                  >
                    <span className="font-medium">{deck.name}</span>
                    {selected && (
                      <span className="ml-2 text-xs text-cyan-600">
                        {deck.cards.length} 枚{" "}
                        {valid ? "✓ 有効" : "✗ 無効"}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* 中央カラム: 手番 + VS + BATTLE */}
        <div className="flex flex-col items-center gap-6 w-48">
          {/* 手番トグル */}
          <div>
            <p className="text-xs font-semibold text-cyan-500 text-center mb-2">
              手番
            </p>
            <div className="flex flex-col items-center gap-1">
              {(
                [
                  { label: "先攻", value: "first" },
                  { label: "後攻", value: "second" },
                  { label: "ランダム", value: "random" },
                ] as { label: string; value: FirstPlayerChoice }[]
              ).map(({ label, value }) => (
                <button
                  key={value}
                  className={[
                    "px-4 py-1 text-sm w-28 transition-colors",
                    firstPlayerChoice === value
                      ? "border-b-2 border-cyan-500 font-semibold text-cyan-600"
                      : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                  onClick={() => setFirstPlayerChoice(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* VS */}
          <p className="text-4xl font-bold tracking-widest text-muted-foreground">
            VS
          </p>

          {/* BATTLE ボタン */}
          <div className="flex flex-col items-center gap-2 w-full">
            <button
              className="w-full py-3 border-l-4 border-cyan-500 bg-foreground text-background font-bold tracking-widest text-lg rounded hover:opacity-90 transition-opacity"
              onClick={handleBattle}
            >
              BATTLE
            </button>
            {/* 選択サマリ */}
            <p className="text-xs text-muted-foreground text-center">
              プレイヤー ✓ {selectedDeck.name} / CPU ✓ ミラー
            </p>
          </div>
        </div>

        {/* 右カラム: CPU */}
        <div className="flex-1 flex flex-col gap-4">
          {/* デッキ preview プレースホルダ */}
          <div className="border border-border rounded-md flex-1 min-h-32 flex items-center justify-center text-muted-foreground text-sm">
            準備中
          </div>

          <div>
            <p className="text-xs font-semibold text-cyan-500 mb-1">CPU</p>
            <p className="text-sm text-muted-foreground mb-2">対戦デッキ</p>

            {/* CPU は mirror 固定 (独自デッキ選択は Phase 2 = 準備中) */}
            <div className="border border-border rounded px-3 py-2 text-sm text-muted-foreground">
              <p>プレイヤーデッキをミラー</p>
              <p className="text-xs mt-1">
                = 同一構成 ({selectedDeck.cards.length} 枚) ✓
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* フッター note */}
      <p className="text-xs text-muted-foreground text-center mt-2">
        BATTLE 押下 → /match 遷移 (engine WASM で SOLO 起動、 server 不要)
      </p>
    </main>
  );
}
